// SunPanel — top-bar slide-in for the time-of-day slider.
//
// Wires the `<input type=range>` in index.html to SunSystem.setTimeOfDay, and
// reads SunSystem state back into the readout + hint lines. Toggle via the ☀
// button in the top bar or the S key.
//
// "Reproduzir o dia" button (Phase 2 polish, 2026-05-14): plays a smooth
// 30-second time-lapse from hour 0 → 24 by advancing the slider every frame.
// Click again to stop. Useful for showing how the same space lives across
// the day to architectural-design audiences.

import type { SunSystem, SunState } from '../playcanvas/sunSystem';

const TIMELAPSE_DURATION_MS = 30_000;
const TIMELAPSE_START_HOUR = 0;
const TIMELAPSE_END_HOUR = 24;

export class SunPanel {
  private readonly panel: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly slider: HTMLInputElement;
  private readonly readout: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly sun: SunSystem;
  private unsub: () => void = () => {};
  private playRaf: number | null = null;
  private playStartedAt = 0;

  constructor(sun: SunSystem) {
    this.sun = sun;
    this.panel = this.requireElement<HTMLElement>('#sun-panel');
    this.toggle = this.requireElement<HTMLButtonElement>('#sun-toggle');
    this.slider = this.requireElement<HTMLInputElement>('#sun-slider');
    this.readout = this.requireElement<HTMLElement>('#sun-panel-readout');
    this.hint = this.requireElement<HTMLElement>('#sun-panel-hint');
    this.closeBtn = this.requireElement<HTMLButtonElement>('#sun-panel-close');
    this.playBtn = this.requireElement<HTMLButtonElement>('#sun-play-button');

    // Seed the slider with the initial state.
    const initial = sun.getState();
    this.slider.value = initial.hoursOfDay.toFixed(2);
    this.renderState(initial);

    this.slider.addEventListener('input', () => {
      // Operator dragging cancels any in-flight time-lapse.
      this.stopTimeLapse();
      const hours = Number(this.slider.value);
      sun.setTimeOfDay(hours);
    });

    this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen()));
    this.closeBtn.addEventListener('click', () => this.setOpen(false));
    this.playBtn.addEventListener('click', () => this.toggleTimeLapse());

    document.addEventListener('keydown', (e) => {
      if (e.key !== 's' && e.key !== 'S') return;
      if (e.target && (e.target as HTMLElement).matches('input, textarea, [contenteditable]')) return;
      this.setOpen(!this.isOpen());
    });

    this.unsub = sun.on((state) => this.renderState(state));
  }

  destroy(): void {
    this.stopTimeLapse();
    this.unsub();
  }

  private isOpen(): boolean {
    return this.panel.classList.contains('is-open');
  }

  private setOpen(open: boolean): void {
    this.panel.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-pressed', String(open));
    this.toggle.classList.toggle('is-active', open);
    if (!open) this.stopTimeLapse();
  }

  private renderState(state: SunState): void {
    // Keep slider in sync if state changed programmatically.
    if (Math.abs(Number(this.slider.value) - state.hoursOfDay) > 0.01) {
      this.slider.value = state.hoursOfDay.toFixed(2);
    }
    const h = Math.floor(state.hoursOfDay);
    const m = Math.round((state.hoursOfDay - h) * 60);
    const clockStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    this.readout.textContent = `${clockStr} · ${labelForElevation(state.elevationDeg)}`;
    this.hint.textContent =
      `Azimute ${Math.round(state.azimuthDeg)}° · elevação ${state.elevationDeg.toFixed(1)}°`;
  }

  // ── Time-lapse ────────────────────────────────────────────────

  private toggleTimeLapse(): void {
    if (this.playRaf !== null) this.stopTimeLapse();
    else this.startTimeLapse();
  }

  private startTimeLapse(): void {
    this.playStartedAt = performance.now();
    this.playBtn.textContent = '■ Parar';
    this.playBtn.classList.add('is-playing');
    const step = (now: number): void => {
      const t = (now - this.playStartedAt) / TIMELAPSE_DURATION_MS;
      if (t >= 1) {
        // Loop back to noon at the end so the room isn't left in the dark.
        this.sun.setTimeOfDay(12);
        this.stopTimeLapse();
        return;
      }
      const hour = TIMELAPSE_START_HOUR + (TIMELAPSE_END_HOUR - TIMELAPSE_START_HOUR) * t;
      this.sun.setTimeOfDay(hour);
      this.playRaf = requestAnimationFrame(step);
    };
    this.playRaf = requestAnimationFrame(step);
  }

  private stopTimeLapse(): void {
    if (this.playRaf !== null) {
      cancelAnimationFrame(this.playRaf);
      this.playRaf = null;
    }
    this.playBtn.textContent = '▶ Reproduzir o dia';
    this.playBtn.classList.remove('is-playing');
  }

  private requireElement<T extends HTMLElement>(selector: string): T {
    const el = document.querySelector<T>(selector);
    if (!el) throw new Error(`[sun-panel] missing element: ${selector}`);
    return el;
  }
}

function labelForElevation(elevDeg: number): string {
  if (elevDeg < -6) return 'noite cerrada';
  if (elevDeg < 0) return 'crepúsculo';
  if (elevDeg < 6) return 'hora dourada';
  if (elevDeg < 30) return 'manhã / fim de tarde';
  if (elevDeg < 60) return 'sol médio';
  return 'sol a pino';
}
